import type { NavigationPermissionRequirement } from '@/types/navigation-permissions';
import type { PermissionAction, PermissionFeature } from '@/utils/permissions';

export type PermissionChecker = (feature: PermissionFeature, action: PermissionAction) => boolean;

export interface NavigationPermissionFilterable {
  requiredPermission?: NavigationPermissionRequirement;
  isSectionHeader?: boolean;
  subItems?: NavigationSubItemFilterable[];
}

export interface NavigationSubItemFilterable {
  requiredPermission?: NavigationPermissionRequirement;
}

const isItemAllowed = (
  permission: NavigationPermissionRequirement | undefined,
  hasPermission: PermissionChecker
): boolean => {
  if (!permission) {
    return true;
  }
  return hasPermission(permission.feature, permission.action);
};

const filterSubItems = <T extends NavigationSubItemFilterable>(
  subItems: T[] | undefined,
  parentPermission: NavigationPermissionRequirement | undefined,
  hasPermission: PermissionChecker
): T[] | undefined => {
  if (!subItems) {
    return undefined;
  }

  const filtered = subItems.filter((subItem) =>
    isItemAllowed(subItem.requiredPermission ?? parentPermission, hasPermission)
  );

  return filtered.length > 0 ? filtered : undefined;
};

const stripEmptySections = <T extends NavigationPermissionFilterable>(items: T[]): T[] => {
  const result: T[] = [];
  let index = 0;

  while (index < items.length) {
    const item = items[index];
    if (!item.isSectionHeader) {
      result.push(item);
      index += 1;
      continue;
    }

    const sectionHeader = item;
    const sectionItems: T[] = [];
    let nextIndex = index + 1;

    while (nextIndex < items.length && !items[nextIndex].isSectionHeader) {
      sectionItems.push(items[nextIndex]);
      nextIndex += 1;
    }

    if (sectionItems.length > 0) {
      result.push(sectionHeader, ...sectionItems);
    }

    index = nextIndex;
  }

  return result;
};

/**
 * Filters navigation trees using backend permissions only.
 * Removes empty sections when all child items are denied.
 */
export const filterNavigationByPermission = <T extends NavigationPermissionFilterable>(
  items: T[],
  hasPermission: PermissionChecker
): T[] => {
  const filteredItems = items
    .map((item) => {
      if (item.isSectionHeader) {
        return item;
      }

      const filteredSubItems = filterSubItems(
        item.subItems,
        item.requiredPermission,
        hasPermission
      );
      const parentAllowed = isItemAllowed(item.requiredPermission, hasPermission);
      const hasVisibleSubItems = Boolean(filteredSubItems?.length);

      if (!parentAllowed && !hasVisibleSubItems) {
        return null;
      }

      if (item.subItems && !hasVisibleSubItems && !parentAllowed) {
        return null;
      }

      return {
        ...item,
        subItems: filteredSubItems,
      };
    })
    .filter((item): item is T => item !== null);

  return stripEmptySections(filteredItems);
};

export const filterFlatNavigationByPermission = <
  T extends { requiredPermission: NavigationPermissionRequirement },
>(
  items: T[],
  hasPermission: PermissionChecker
): T[] => items.filter((item) => isItemAllowed(item.requiredPermission, hasPermission));
